import { Box, Typography } from "@mui/material";
import type { FC, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { FONT_SIZE } from "../../theme/tokens";
import type { WageBreakdownResult } from "../../utils/models";
import { getShortRole } from "../../utils/utilFunctions";
import { CompositionToggle } from "./CompositionToggle";
import { HeaderTotal } from "./HeaderTotal";
import { headerBandSx } from "./headerSx";
import { SCOPE_SEPARATOR } from "./scopeText";
import { useHeaderConstants } from "./useHeaderConstants";
import type { BreakdownNurse } from "./WageBreakdownFooterLinks";
import type { WageComposition } from "./wageComposition";

type Props = {
	breakdown: WageBreakdownResult;
	composition: WageComposition;
	/** Absent where the caller has no nurse to name: the panel's own title stands in. */
	nurse?: BreakdownNurse;
	/** The shift's paid hours, as the row's hours column shows them. */
	paidHours: number;
	canViewWage: boolean;
	/** The fold toggle, when the shift has more segments than open comfortably. */
	toggle?: { open: boolean; onToggle: () => void; controls: string };
	/** Legacy breakdowns only: where a trainee's/orientee's base came from, beside the base it set. */
	legacyTrainingLink?: ReactNode;
};

/**
 * The breakdown's summary band: whose shift it is and what is constant across it on the left, and the
 * total on the right.
 */
export const WageBreakdownHeader: FC<Props> = ({ breakdown, composition, nurse, paidHours, canViewWage, toggle, legacyTrainingLink }) => {
	const { t } = useTranslation();
	const constants = useHeaderConstants(breakdown, composition, paidHours, canViewWage);
	// DEFAULT: a caller with no nurse (none today) still gets a heading — the panel's own title.
	const heading = nurse ? [nurse.name, getShortRole(nurse.role)].join(SCOPE_SEPARATOR) : t("wageBreakdown.title");
	return (
		<Box data-testid="wage-breakdown-header" sx={headerBandSx}>
			<Box sx={{ minWidth: 0, flex: "1 1 16rem" }}>
				{/* Whose shift, then its constants, on one line — wrapping under the name when the panel is narrow. */}
				<Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "baseline", columnGap: 1.5, rowGap: 0.25 }}>
					<Typography data-testid="wage-breakdown-header-heading" component="h3" sx={{ fontSize: FONT_SIZE.EMPHASIS, fontWeight: 700, color: "text.primary" }}>
						{heading}
					</Typography>
					<Typography
						data-testid="wage-breakdown-header-constants"
						sx={{ fontSize: FONT_SIZE.SECONDARY, color: "text.secondary", fontVariantNumeric: "tabular-nums" }}
					>
						{constants}
						{legacyTrainingLink && constants.length > 0 && (
							<Box component="span" aria-hidden sx={{ color: "text.disabled", mx: 0.75 }}>
								·
							</Box>
						)}
						{legacyTrainingLink}
					</Typography>
				</Box>
				{toggle && <CompositionToggle open={toggle.open} onToggle={toggle.onToggle} controls={toggle.controls} />}
			</Box>
			<HeaderTotal totalCents={composition.totalCents} bonusCents={composition.bonusCents} canViewWage={canViewWage} />
		</Box>
	);
};
